import { Text, Title } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { SearchLine } from "@dropbox/dig-icons/assets";
import { useIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { fetchSearchResults } from "@mirage/service-dbx-api";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { DEBUG_INPUT_ID, DebuggerHeader } from "./DebuggerHeader";
import { DevToolsPanel } from "./DevToolsPanel";
import styles from "./SearchDebuggerPage.module.css";
import { PanelConfig, PanelResult, SearchPanel } from "./SearchPanel";

interface PanelState {
  id: string;
  title: string;
  config?: PanelConfig;
  result: PanelResult;
}

const MAX_PANELS = 2;
const PANEL_ID_BASELINE = "baseline";

const EmptyState = () => {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateContent}>
        <UIIcon src={SearchLine} size="large" />
        <Title>Ready to debug your search results?</Title>
        <div className={styles.emptyStateDescription}>
          <Text>
            <strong>Quick search:</strong> Enter a query above and submit to see
            results with enhanced diagnostics on the result set
          </Text>
          <Text>
            <strong>Add magic flags:</strong> Click the settings gear next to
            the panel header to enter &quot;magic flags&quot;
          </Text>
        </div>
      </div>
    </div>
  );
};

export const SearchDebuggerPage = () => {
  const isDropboxer = useIsDropboxer();
  const [query, setQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<SearchFilter[]>([]);
  const [selectedResult, setSelectedResult] = useState<{
    panelId: string;
    result: SearchResult;
  } | null>(null);

  // Start with just control panel
  const [panels, setPanels] = useState<PanelState[]>([
    {
      id: PANEL_ID_BASELINE,
      title: "Baseline",
      config: {
        magicFlags: {},
        isConfigExpanded: true,
      },
      result: { results: [], loading: false },
    },
    {
      id: uuidv4(),
      title: "Experiment",
      config: {
        magicFlags: {},
        isConfigExpanded: true,
      },
      result: { results: [], loading: false },
    },
  ]);

  // Function to add magic flags to query similar to useMultiAnswers
  const addMagicFlags = useCallback(
    (baseQuery: string, magicFlags: Record<string, string>) => {
      let modifiedQuery = baseQuery;
      const magicFlagsList = Object.entries(magicFlags);

      if (magicFlagsList.length > 0) {
        const magicFlagsString = JSON.stringify(magicFlags);
        modifiedQuery += " #answers_experimental_flags=" + magicFlagsString;
      }

      return modifiedQuery;
    },
    [],
  );

  // only dropboxers allowed in the club
  if (!isDropboxer) {
    return null;
  }

  // Check if any panel is loading
  const isLoading = panels.some((panel) => panel.result.loading);

  // Check if we should show empty state (no query and no results loaded)
  const shouldShowEmptyState = panels.every(
    (panel) => panel.result.results.length === 0 && !panel.result.loading,
  );

  const handleSelectResult = ({
    result,
    panelId,
  }: {
    result: SearchResult;
    panelId: string;
  }) => {
    setSelectedResult({ result, panelId });
  };

  const handleCloseDevTools = () => {
    setSelectedResult(null);
  };

  const isResultSelected = ({
    result,
    panelId,
  }: {
    result: SearchResult;
    panelId: string;
  }) => {
    return (
      selectedResult?.result.uuid === result.uuid &&
      selectedResult?.panelId === panelId
    );
  };

  const handleAddPanel = () => {
    if (panels.length >= MAX_PANELS) return;

    const newPanel: PanelState = {
      id: uuidv4(),
      title: "Experiment",
      config: {
        magicFlags: {},
        isConfigExpanded: true,
      },
      result: { results: [], loading: false },
    };

    setPanels((prevPanels) => [...prevPanels, newPanel]);
    document.getElementById(DEBUG_INPUT_ID)?.focus();
  };

  const removePanel = (panelId: string) => {
    setPanels((prevPanels) => prevPanels.filter((p) => p.id !== panelId));
  };

  const handleSubmit = async (filters?: SearchFilter[]) => {
    if (!query.trim() && isLoading) {
      return;
    }

    // Set loading state for all panels and collapse config panels
    setPanels((prevPanels) =>
      prevPanels.map((panel) => ({
        ...panel,
        config: panel.config
          ? { ...panel.config, isConfigExpanded: false }
          : panel.config,
        result: { ...panel.result, loading: true, error: undefined },
      })),
    );

    // Make API calls for each panel
    const panelPromises = panels.map(async (panel) => {
      try {
        // Build the query with magic flags for this panel
        let queryForPanel = query;
        if (
          panel.config?.magicFlags &&
          Object.keys(panel.config.magicFlags).length > 0
        ) {
          queryForPanel = addMagicFlags(query, panel.config.magicFlags);
        }

        // Capture API latency
        const startTime = performance.now();
        const { results } = await fetchSearchResults(
          queryForPanel,
          filters || activeFilters,
          true, // disable spell correction
        );
        const endTime = performance.now();
        const latency = Math.round(endTime - startTime);

        return {
          results: results.slice(0, 50), // To keep the UI perf snappy until we do some windowing
          loading: false,
          error: undefined,
          latency,
        };
      } catch (error) {
        return {
          results: [],
          loading: false,
          error: error instanceof Error ? error.message : "Unknown error",
          latency: undefined,
        };
      }
    });

    try {
      const panelResults = await Promise.all(panelPromises);

      setPanels((prevPanels) =>
        prevPanels.map((panel, index) => ({
          ...panel,
          result: panelResults[index],
        })),
      );
    } catch (error) {
      // TODO: handle error
    }
  };

  const handleFilterChange = async (filters: SearchFilter[]) => {
    setActiveFilters(filters);
    handleSubmit(filters);
  };

  const handlePanelConfigChange = (panelId: string, config: PanelConfig) => {
    setPanels((prevPanels) =>
      prevPanels.map((panel) =>
        panel.id === panelId ? { ...panel, config } : panel,
      ),
    );
  };

  return (
    <div className={styles.page}>
      <DebuggerHeader
        query={query}
        onQueryChange={(newQuery) => setQuery(newQuery)}
        onSubmit={handleSubmit}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        showAddPanelButton={panels.length < MAX_PANELS}
        onAddPanel={handleAddPanel}
        loading={isLoading}
      />

      <div className={styles.panelsContainer}>
        <div
          className={styles.panels}
          style={{ gridTemplateColumns: `repeat(${panels.length}, 1fr)` }}
        >
          {panels.map((panel) => (
            <SearchPanel
              query={query}
              panelId={panel.id}
              key={panel.id}
              title={panel.title}
              config={panel.config}
              onConfigChange={(config) =>
                handlePanelConfigChange(panel.id, config)
              }
              result={panel.result}
              onRemove={
                panel.id === PANEL_ID_BASELINE
                  ? undefined
                  : () => removePanel(panel.id)
              }
              onSelectResult={handleSelectResult}
              isResultSelected={isResultSelected}
            />
          ))}
        </div>

        {shouldShowEmptyState && <EmptyState />}
      </div>

      {selectedResult?.result && (
        <DevToolsPanel
          selectedResult={selectedResult.result}
          onClose={handleCloseDevTools}
        />
      )}
    </div>
  );
};
